// features/inventory/pages/ValuationDashboardPage.tsx
/**
 * Valuation Dashboard Page — Aggregate inventory value across all products and branches.
 *
 * Route: /inventory/valuation
 *
 * Features:
 * - Total inventory value summary cards with comparative context
 * - Branch breakdown with progress bars and percentages
 * - Category breakdown with distribution
 * - Top 10 products by value
 * - Full valuation table with pagination, search, and sorting
 * - Cost vs Retail comparison
 * - Branch filter
 * - Export valuation report
 */

import { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { useToastStore } from '@/store/toastStore';
import { Card, CardContent } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import { Pagination } from '@/components/ui/Pagination';
import { getStockValuation } from '@/features/stock/services/stockApi';
import { getBranches } from '@/features/settings/services/settingsApi';
import { getCategories } from '@/features/products/services/productsApi';
import { getProducts, ProductData } from '@/features/products/services/productsApi';
import {
    ArrowLeft,
    DollarSign,
    TrendingUp,
    Package,
    Store,
    RefreshCw,
    Download,
    Layers,
    Calculator,
    ChevronRight,
    Search,
    X,
    ArrowUpDown,
    ArrowUp,
    ArrowDown,
    FileSpreadsheet,
    PieChart,
    BarChart3,
    ShoppingCart,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';

// ============================================================
// TYPES
// ============================================================

interface BranchValuation {
    branchId: string;
    branchName: string;
    productCount: number;
    totalCost: number;
    totalRetail: number;
    totalProfit: number;
    percentOfTotal: number;
}

interface CategoryValuation {
    categoryId: string;
    categoryName: string;
    productCount: number;
    totalCost: number;
    percentOfTotal: number;
}

interface TopProduct {
    productId: string;
    productName: string;
    branchName: string;
    quantityOnHand: string;
    costPrice: string;
    totalValue: string;
    retailPrice: string;
    totalRetailValue: string;
    potentialProfit: string;
}

type SortField = 'productName' | 'quantityOnHand' | 'costPrice' | 'totalValue' | 'totalRetailValue' | 'potentialProfit';
type SortOrder = 'asc' | 'desc';

// ============================================================
// CONSTANTS
// ============================================================

const PAGE_SIZE_OPTIONS = [12, 24, 48, 100];
const DEFAULT_PAGE_SIZE = 24;

// ============================================================
// MAIN COMPONENT
// ============================================================

export function ValuationDashboardPage() {
    const navigate = useNavigate();
    const user = useAuthStore((s) => s.user);
    const selectedBranchId = useAuthStore((s) => s.selectedBranchId);
    const addToast = useToastStore((s) => s.addToast);

    // State
    const [branchId, setBranchId] = useState(selectedBranchId || '');
    const [searchQuery, setSearchQuery] = useState('');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [sortBy, setSortBy] = useState<SortField>('totalValue');
    const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
    const [activeView, setActiveView] = useState<'overview' | 'products'>('overview');

    // Queries
    const { data: branches } = useQuery({
        queryKey: ['branches-valuation', user?.tenantId],
        queryFn: async () => {
            try {
                const data = await getBranches(user!.tenantId);
                return Array.isArray(data) ? data : [];
            } catch {
                return [];
            }
        },
        enabled: !!user?.tenantId,
    });

    const { data: valuation, isLoading, isError, refetch } = useQuery({
        queryKey: ['stock-valuation-all', user?.tenantId, branchId],
        queryFn: () => getStockValuation(user!.tenantId, branchId || undefined),
        enabled: !!user?.tenantId,
    });

    const { data: categories } = useQuery({
        queryKey: ['categories-valuation', user?.tenantId],
        queryFn: async () => {
            try {
                return await getCategories(user!.tenantId);
            } catch {
                return [];
            }
        },
        enabled: !!user?.tenantId,
    });

    // Derived data
    const branchesList = (branches || []) as Array<{ id: string; name: string }>;
    const valuationData = Array.isArray(valuation) ? valuation : [];
    const categoriesList = Array.isArray(categories) ? categories : [];

    // Total valuation
    const totalStats = useMemo(() => {
        const totalCost = valuationData.reduce(
            (sum: number, item: any) => sum + parseFloat(item.totalValue || '0'),
            0
        );
        const totalRetail = valuationData.reduce(
            (sum: number, item: any) => sum + parseFloat(item.totalRetailValue || '0'),
            0
        );
        const totalProfit = valuationData.reduce(
            (sum: number, item: any) => sum + parseFloat(item.potentialProfit || '0'),
            0
        );
        const totalProducts = valuationData.length;
        const marginPercent =
            totalRetail > 0 ? ((totalProfit / totalRetail) * 100).toFixed(1) : '0';

        return {
            totalCost,
            totalRetail,
            totalProfit,
            totalProducts,
            marginPercent,
        };
    }, [valuationData]);

    // Branch breakdown
    const branchBreakdown: BranchValuation[] = useMemo(() => {
        const branchMap = new Map<string, BranchValuation>();

        valuationData.forEach((item: any) => {
            const bId = item.branchId || 'unknown';
            const bName = item.branchName || 'Unknown';
            const existing = branchMap.get(bId);

            const cost = parseFloat(item.totalValue || '0');
            const retail = parseFloat(item.totalRetailValue || '0');
            const profit = parseFloat(item.potentialProfit || '0');

            if (!existing) {
                branchMap.set(bId, {
                    branchId: bId,
                    branchName: bName,
                    productCount: 1,
                    totalCost: cost,
                    totalRetail: retail,
                    totalProfit: profit,
                    percentOfTotal: 0,
                });
            } else {
                existing.productCount++;
                existing.totalCost += cost;
                existing.totalRetail += retail;
                existing.totalProfit += profit;
            }
        });

        const result = Array.from(branchMap.values());
        const grandTotal = totalStats.totalCost;

        result.forEach((branch) => {
            branch.percentOfTotal =
                grandTotal > 0
                    ? parseFloat(((branch.totalCost / grandTotal) * 100).toFixed(1))
                    : 0;
        });

        return result.sort((a, b) => b.totalCost - a.totalCost);
    }, [valuationData, totalStats.totalCost]);

    // Category breakdown
    const categoryBreakdown: CategoryValuation[] = useMemo(() => {
        const categoryMap = new Map<string, CategoryValuation>();

        valuationData.forEach((item: any) => {
            const catId = item.categoryId || 'uncategorized';
            const catName = item.categoryName || 'Uncategorized';
            const existing = categoryMap.get(catId);
            const cost = parseFloat(item.totalValue || '0');

            if (!existing) {
                categoryMap.set(catId, {
                    categoryId: catId,
                    categoryName: catName,
                    productCount: 1,
                    totalCost: cost,
                    percentOfTotal: 0,
                });
            } else {
                existing.productCount++;
                existing.totalCost += cost;
            }
        });

        const result = Array.from(categoryMap.values());
        const grandTotal = totalStats.totalCost;

        result.forEach((category) => {
            category.percentOfTotal =
                grandTotal > 0
                    ? parseFloat(((category.totalCost / grandTotal) * 100).toFixed(1))
                    : 0;
        });

        return result.sort((a, b) => b.totalCost - a.totalCost);
    }, [valuationData, totalStats.totalCost]);

    // Filtered and sorted valuation data for table
    const filteredValuationData = useMemo(() => {
        let filtered = [...valuationData];

        // Apply search filter
        if (searchQuery) {
            const q = searchQuery.toLowerCase();
            filtered = filtered.filter((item: any) =>
                (item.productName || '').toLowerCase().includes(q) ||
                (item.sku || '').toLowerCase().includes(q) ||
                (item.branchName || '').toLowerCase().includes(q)
            );
        }

        // Apply sorting
        filtered.sort((a: any, b: any) => {
            let aVal: any, bVal: any;

            switch (sortBy) {
                case 'productName':
                    aVal = (a.productName || '').toLowerCase();
                    bVal = (b.productName || '').toLowerCase();
                    break;
                case 'quantityOnHand':
                    aVal = parseFloat(a.quantityOnHand || '0');
                    bVal = parseFloat(b.quantityOnHand || '0');
                    break;
                case 'costPrice':
                    aVal = parseFloat(a.costPrice || '0');
                    bVal = parseFloat(b.costPrice || '0');
                    break;
                case 'totalRetailValue':
                    aVal = parseFloat(a.totalRetailValue || '0');
                    bVal = parseFloat(b.totalRetailValue || '0');
                    break;
                case 'potentialProfit':
                    aVal = parseFloat(a.potentialProfit || '0');
                    bVal = parseFloat(b.potentialProfit || '0');
                    break;
                case 'totalValue':
                default:
                    aVal = parseFloat(a.totalValue || '0');
                    bVal = parseFloat(b.totalValue || '0');
                    break;
            }

            if (sortOrder === 'asc') {
                return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
            } else {
                return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
            }
        });

        return filtered;
    }, [valuationData, searchQuery, sortBy, sortOrder]);

    // Paginated data
    const totalPages = Math.max(1, Math.ceil(filteredValuationData.length / pageSize));
    const paginatedData = filteredValuationData.slice(
        (page - 1) * pageSize,
        page * pageSize
    );

    // Top 10 products by value
    const topProducts: TopProduct[] = useMemo(() => {
        return [...valuationData]
            .sort(
                (a: any, b: any) =>
                    parseFloat(b.totalValue || '0') - parseFloat(a.totalValue || '0')
            )
            .slice(0, 10)
            .map((item: any) => ({
                productId: item.productId,
                productName: item.productName || 'Unknown',
                branchName: item.branchName || 'Unknown',
                quantityOnHand: item.quantityOnHand || '0',
                costPrice: item.costPrice || '0',
                totalValue: item.totalValue || '0',
                retailPrice: item.retailPrice || '0',
                totalRetailValue: item.totalRetailValue || '0',
                potentialProfit: item.potentialProfit || '0',
            }));
    }, [valuationData]);

    const getBranchName = (id: string) =>
        branchesList.find((b) => b.id === id)?.name || 'All Branches';

    const formatNaira = (val: number | string): string => {
        const num = typeof val === 'string' ? parseFloat(val) : val;
        if (isNaN(num)) return '₦0';
        if (num >= 1_000_000) {
            return `₦${(num / 1_000_000).toFixed(2)}M`;
        }
        return `₦${num.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
    };

    const formatNumber = (val: string | undefined): string => {
        if (!val) return '0';
        const num = parseFloat(val);
        return isNaN(num)
            ? '0'
            : num.toLocaleString(undefined, { maximumFractionDigits: 0 });
    };

    const handleSort = (field: SortField) => {
        if (sortBy === field) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(field);
            setSortOrder('desc');
        }
        setPage(1);
    };

    const handleExport = () => {
        try {
            const csv = filteredValuationData.map((item: any) => ({
                'Product': item.productName || 'Unknown',
                'SKU': item.sku || '',
                'Branch': item.branchName || 'Unknown',
                'Quantity': item.quantityOnHand || '0',
                'Cost Price': item.costPrice || '0',
                'Total Cost': item.totalValue || '0',
                'Retail Price': item.retailPrice || '0',
                'Total Retail': item.totalRetailValue || '0',
                'Potential Profit': item.potentialProfit || '0',
            }));

            const csvString = [
                Object.keys(csv[0] || {}).join(','),
                ...csv.map(row => Object.values(row).join(','))
            ].join('\n');

            const blob = new Blob([csvString], { type: 'text/csv' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `valuation-${new Date().toISOString().split('T')[0]}.csv`;
            a.click();
            URL.revokeObjectURL(url);

            addToast({
                type: 'success',
                title: 'Valuation exported',
                message: `${filteredValuationData.length} products exported to CSV`,
            });
        } catch {
            addToast({ type: 'error', title: 'Export failed' });
        }
    };

    const SortableHeader = ({ field, label, className }: { field: SortField; label: string; className?: string }) => (
        <th
            onClick={() => handleSort(field)}
            className={clsx(
                'px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase cursor-pointer hover:bg-slate-100 transition-colors select-none',
                className
            )}
        >
            <span className="inline-flex items-center gap-1">
                {label}
                {sortBy === field ? (
                    sortOrder === 'asc' ? (
                        <ArrowUp className="w-3 h-3" />
                    ) : (
                        <ArrowDown className="w-3 h-3" />
                    )
                ) : (
                    <ArrowUpDown className="w-3 h-3 opacity-30" />
                )}
            </span>
        </th>
    );

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/30">
            {/* Header */}
            <div className="bg-white/90 backdrop-blur-md border-b border-slate-200 sticky top-0 z-30 shadow-sm">
                <div className="max-w-[1600px] mx-auto px-6 lg:px-8 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <button
                                onClick={() => navigate('/inventory')}
                                className="p-2 rounded-xl hover:bg-slate-100 transition-colors"
                                type="button"
                            >
                                <ArrowLeft className="w-5 h-5 text-slate-600" />
                            </button>
                            <div className="p-2.5 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl shadow-lg shadow-emerald-500/25">
                                <Calculator className="w-6 h-6 text-white" />
                            </div>
                            <div>
                                <h1 className="text-2xl font-bold text-slate-900">
                                    Stock Valuation
                                </h1>
                                <p className="text-sm text-slate-500">
                                    {getBranchName(branchId)} ·{' '}
                                    {totalStats.totalProducts.toLocaleString()} products · Total:{' '}
                                    {formatNaira(totalStats.totalCost)}
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={handleExport}
                                className="flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 transition-all text-sm font-medium text-slate-700 shadow-sm"
                                type="button"
                            >
                                <FileSpreadsheet className="w-4 h-4" /> Export CSV
                            </button>
                            <button
                                onClick={() => refetch()}
                                className="flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 transition-all text-sm font-medium text-slate-700 shadow-sm"
                                type="button"
                            >
                                <RefreshCw className="w-4 h-4" /> Refresh
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-[1600px] mx-auto p-6 lg:p-8 space-y-5">
                {/* Branch Filter */}
                {branchesList.length > 1 && (
                    <div className="flex items-center gap-3">
                        <Store className="w-4 h-4 text-slate-400" />
                        <select
                            value={branchId}
                            onChange={(e) => {
                                setBranchId(e.target.value);
                                setPage(1);
                            }}
                            className="px-4 py-2.5 border-2 border-slate-200 rounded-xl text-sm bg-white focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400 font-medium text-slate-700"
                        >
                            <option value="">All Branches</option>
                            {branchesList.map((b) => (
                                <option key={b.id} value={b.id}>
                                    {b.name}
                                </option>
                            ))}
                        </select>
                    </div>
                )}

                {isLoading ? (
                    <div className="flex justify-center py-20">
                        <Spinner size="xl" />
                    </div>
                ) : isError ? (
                    <ErrorState
                        title="Failed to load valuation data"
                        onRetry={() => refetch()}
                    />
                ) : valuationData.length === 0 ? (
                    <EmptyState
                        icon={<DollarSign className="w-16 h-16" />}
                        title="No valuation data"
                        description="Upload stock with cost prices to see inventory valuation."
                        action={{
                            label: 'Set Up Stock',
                            onClick: () => navigate('/inventory/setup'),
                        }}
                    />
                ) : (
                    <>
                        {/* ========== DESCRIPTIVE VALUATION STATS ========== */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                                className="bg-white rounded-2xl border-2 border-emerald-200 shadow-sm p-5 hover:shadow-lg transition-all">
                                <div className="flex items-center justify-between mb-2">
                                    <div className="p-3 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 shadow-md"><DollarSign className="w-5 h-5 text-white" /></div>
                                    <span className="text-[10px] text-emerald-600 font-semibold">Cost Basis</span>
                                </div>
                                <p className="text-xs text-slate-500 uppercase tracking-wider">Total Inventory Value</p>
                                <p className="text-3xl font-black text-slate-900 mt-1">{formatNaira(totalStats.totalCost)}</p>
                                <p className="text-xs text-slate-400 mt-1">{totalStats.totalProducts.toLocaleString()} products · {branchBreakdown.length} branches</p>
                            </motion.div>

                            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                                className="bg-white rounded-2xl border-2 border-blue-200 shadow-sm p-5 hover:shadow-lg transition-all">
                                <div className="flex items-center justify-between mb-2">
                                    <div className="p-3 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 shadow-md"><TrendingUp className="w-5 h-5 text-white" /></div>
                                    <span className="text-[10px] text-blue-600 font-semibold">Potential Revenue</span>
                                </div>
                                <p className="text-xs text-slate-500 uppercase tracking-wider">Total Retail Value</p>
                                <p className="text-3xl font-black text-slate-900 mt-1">{formatNaira(totalStats.totalRetail)}</p>
                                <p className="text-xs text-slate-400 mt-1">{totalStats.totalCost > 0 ? `${Math.round((totalStats.totalRetail / totalStats.totalCost) * 100)}% markup on cost` : 'No cost data'}</p>
                            </motion.div>

                            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                                className="bg-white rounded-2xl border-2 border-purple-200 shadow-sm p-5 hover:shadow-lg transition-all">
                                <div className="flex items-center justify-between mb-2">
                                    <div className="p-3 rounded-xl bg-gradient-to-br from-purple-500 to-violet-600 shadow-md"><TrendingUp className="w-5 h-5 text-white" /></div>
                                    <span className="text-[10px] text-purple-600 font-semibold">{totalStats.marginPercent}% margin</span>
                                </div>
                                <p className="text-xs text-slate-500 uppercase tracking-wider">Potential Profit</p>
                                <p className="text-3xl font-black text-emerald-700 mt-1">{formatNaira(totalStats.totalProfit)}</p>
                                <p className="text-xs text-slate-400 mt-1">If all stock sold at retail price</p>
                            </motion.div>

                            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                                className="bg-white rounded-2xl border-2 border-amber-200 shadow-sm p-5 hover:shadow-lg transition-all">
                                <div className="flex items-center justify-between mb-2">
                                    <div className="p-3 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 shadow-md"><Layers className="w-5 h-5 text-white" /></div>
                                    <span className="text-[10px] text-amber-600 font-semibold">Active Locations</span>
                                </div>
                                <p className="text-xs text-slate-500 uppercase tracking-wider">Branches with Stock</p>
                                <p className="text-3xl font-black text-slate-900 mt-1">{branchBreakdown.length}</p>
                                <p className="text-xs text-slate-400 mt-1">{branchBreakdown.length > 1 ? `${branchBreakdown[0]?.branchName || 'Top branch'} has ${branchBreakdown[0]?.percentOfTotal || 0}% of value` : 'All stock in one location'}</p>
                            </motion.div>
                        </div>

                        {/* View Toggle */}
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => setActiveView('overview')}
                                className={clsx(
                                    'flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all',
                                    activeView === 'overview'
                                        ? 'bg-emerald-600 text-white shadow-md'
                                        : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
                                )}
                                type="button"
                            >
                                <PieChart className="w-4 h-4" /> Overview
                            </button>
                            <button
                                onClick={() => setActiveView('products')}
                                className={clsx(
                                    'flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all',
                                    activeView === 'products'
                                        ? 'bg-emerald-600 text-white shadow-md'
                                        : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
                                )}
                                type="button"
                            >
                                <BarChart3 className="w-4 h-4" /> All Products
                            </button>
                        </div>

                        {activeView === 'overview' ? (
                            <>
                                {/* Branch & Category Distribution */}
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                    {/* Branch Breakdown */}
                                    <Card className="overflow-hidden">
                                        <div className="px-6 py-4 bg-gradient-to-r from-emerald-50 to-teal-50 border-b border-emerald-100">
                                            <div className="flex items-center justify-between">
                                                <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                                                    <div className="p-1.5 rounded-lg bg-emerald-100">
                                                        <Store className="w-4 h-4 text-emerald-600" />
                                                    </div>
                                                    Value by Branch
                                                </h3>
                                                <Badge variant="success" size="sm">
                                                    {branchBreakdown.length} locations
                                                </Badge>
                                            </div>
                                        </div>
                                        <CardContent className="p-6">
                                            <div className="space-y-5">
                                                {branchBreakdown.map((branch, idx) => (
                                                    <motion.div
                                                        key={branch.branchId}
                                                        initial={{ opacity: 0, x: -20 }}
                                                        animate={{ opacity: 1, x: 0 }}
                                                        transition={{ delay: idx * 0.1 }}
                                                    >
                                                        <div className="flex items-center justify-between mb-2">
                                                            <div className="flex items-center gap-3">
                                                                <div className={clsx(
                                                                    'w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold',
                                                                    idx === 0 ? 'bg-amber-100 text-amber-700' :
                                                                        idx === 1 ? 'bg-slate-200 text-slate-700' :
                                                                            idx === 2 ? 'bg-orange-100 text-orange-700' :
                                                                                'bg-slate-100 text-slate-500'
                                                                )}>
                                                                    #{idx + 1}
                                                                </div>
                                                                <div>
                                                                    <p className="text-sm font-semibold text-slate-700">
                                                                        {branch.branchName}
                                                                    </p>
                                                                    <p className="text-[10px] text-slate-400">
                                                                        {branch.productCount} products · {formatNaira(branch.totalRetail)} retail
                                                                    </p>
                                                                </div>
                                                            </div>
                                                            <div className="text-right">
                                                                <p className="text-sm font-bold text-slate-700">
                                                                    {formatNaira(branch.totalCost)}
                                                                </p>
                                                                <p className="text-[10px] font-medium text-emerald-600">
                                                                    {branch.percentOfTotal}% of total
                                                                </p>
                                                            </div>
                                                        </div>
                                                        <div className="w-full bg-slate-100 rounded-full h-3 overflow-hidden">
                                                            <motion.div
                                                                className={clsx(
                                                                    'h-3 rounded-full',
                                                                    idx === 0 ? 'bg-gradient-to-r from-amber-400 to-orange-500' :
                                                                        'bg-gradient-to-r from-emerald-500 to-teal-600'
                                                                )}
                                                                initial={{ width: 0 }}
                                                                animate={{ width: `${branch.percentOfTotal}%` }}
                                                                transition={{ duration: 1, delay: idx * 0.1, ease: 'easeOut' }}
                                                            />
                                                        </div>
                                                    </motion.div>
                                                ))}
                                            </div>
                                        </CardContent>
                                    </Card>

                                    {/* Category Breakdown */}
                                    {categoryBreakdown.length > 0 && (
                                        <Card className="overflow-hidden">
                                            <div className="px-6 py-4 bg-gradient-to-r from-purple-50 to-violet-50 border-b border-purple-100">
                                                <div className="flex items-center justify-between">
                                                    <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                                                        <div className="p-1.5 rounded-lg bg-purple-100">
                                                            <PieChart className="w-4 h-4 text-purple-600" />
                                                        </div>
                                                        Value by Category
                                                    </h3>
                                                    <Badge variant="info" size="sm">
                                                        {categoryBreakdown.length} categories
                                                    </Badge>
                                                </div>
                                            </div>
                                            <CardContent className="p-6">
                                                <div className="space-y-4">
                                                    {categoryBreakdown.slice(0, 5).map((category, idx) => (
                                                        <motion.div
                                                            key={category.categoryId}
                                                            initial={{ opacity: 0, x: 20 }}
                                                            animate={{ opacity: 1, x: 0 }}
                                                            transition={{ delay: idx * 0.1 }}
                                                            className="p-4 rounded-xl bg-gradient-to-r from-slate-50 to-white border border-slate-100 hover:border-purple-200 transition-all hover:shadow-sm"
                                                        >
                                                            <div className="flex items-center justify-between mb-2">
                                                                <div className="flex items-center gap-2">
                                                                    <div className={clsx(
                                                                        'w-2.5 h-2.5 rounded-full',
                                                                        idx === 0 ? 'bg-purple-500' :
                                                                            idx === 1 ? 'bg-blue-500' :
                                                                                idx === 2 ? 'bg-emerald-500' :
                                                                                    idx === 3 ? 'bg-amber-500' :
                                                                                        'bg-slate-400'
                                                                    )} />
                                                                    <p className="text-sm font-semibold text-slate-700">
                                                                        {category.categoryName}
                                                                    </p>
                                                                </div>
                                                                <span className="text-xs font-bold text-slate-700">
                                                                    {formatNaira(category.totalCost)}
                                                                </span>
                                                            </div>
                                                            <div className="flex items-center justify-between text-[10px] text-slate-400 mb-1.5">
                                                                <span>{category.productCount} products</span>
                                                                <span className="font-semibold text-purple-600">{category.percentOfTotal}%</span>
                                                            </div>
                                                            <div className="w-full bg-slate-200 rounded-full h-2 overflow-hidden">
                                                                <motion.div
                                                                    className={clsx(
                                                                        'h-2 rounded-full',
                                                                        idx === 0 ? 'bg-gradient-to-r from-purple-500 to-violet-600' :
                                                                            idx === 1 ? 'bg-gradient-to-r from-blue-500 to-indigo-600' :
                                                                                idx === 2 ? 'bg-gradient-to-r from-emerald-500 to-teal-600' :
                                                                                    idx === 3 ? 'bg-gradient-to-r from-amber-500 to-orange-600' :
                                                                                        'bg-gradient-to-r from-slate-400 to-slate-500'
                                                                    )}
                                                                    initial={{ width: 0 }}
                                                                    animate={{ width: `${category.percentOfTotal}%` }}
                                                                    transition={{ duration: 1, delay: idx * 0.1, ease: 'easeOut' }}
                                                                />
                                                            </div>
                                                        </motion.div>
                                                    ))}
                                                </div>
                                                {categoryBreakdown.length > 5 && (
                                                    <button className="mt-4 text-xs font-medium text-purple-600 hover:text-purple-700 flex items-center gap-1">
                                                        View all {categoryBreakdown.length} categories
                                                        <ChevronRight className="w-3.5 h-3.5" />
                                                    </button>
                                                )}
                                            </CardContent>
                                        </Card>
                                    )}
                                </div>

                                {/* Top Products & Right Side Panel */}
                                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                                    {/* Top 10 Products - Takes 2/3 width */}
                                    <Card className="lg:col-span-2 overflow-hidden">
                                        <div className="px-6 py-4 bg-gradient-to-r from-amber-50 to-orange-50 border-b border-amber-100">
                                            <div className="flex items-center justify-between">
                                                <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                                                    <div className="p-1.5 rounded-lg bg-amber-100">
                                                        <Package className="w-4 h-4 text-amber-600" />
                                                    </div>
                                                    Top Products by Value
                                                </h3>
                                                <Badge variant="warning" size="sm">
                                                    Top 10
                                                </Badge>
                                            </div>
                                        </div>
                                        <CardContent className="p-4">
                                            <div className="space-y-2">
                                                {topProducts.map((product, idx) => (
                                                    <motion.div
                                                        key={product.productId}
                                                        initial={{ opacity: 0, y: 10 }}
                                                        animate={{ opacity: 1, y: 0 }}
                                                        transition={{ delay: idx * 0.05 }}
                                                        onClick={() => navigate(`/inventory/levels/${product.productId}`)}
                                                        className="flex items-center justify-between p-3 rounded-xl hover:bg-gradient-to-r hover:from-amber-50 hover:to-orange-50 cursor-pointer transition-all group border border-transparent hover:border-amber-200"
                                                    >
                                                        <div className="flex items-center gap-3 flex-1 min-w-0">
                                                            <div className={clsx(
                                                                'w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold flex-shrink-0',
                                                                idx === 0 ? 'bg-amber-400 text-white shadow-md' :
                                                                    idx === 1 ? 'bg-slate-400 text-white' :
                                                                        idx === 2 ? 'bg-orange-400 text-white' :
                                                                            'bg-slate-100 text-slate-500'
                                                            )}>
                                                                {idx + 1}
                                                            </div>
                                                            <div className="min-w-0 flex-1">
                                                                <p className="text-sm font-semibold text-slate-700 truncate group-hover:text-amber-700 transition-colors">
                                                                    {product.productName}
                                                                </p>
                                                                <p className="text-[10px] text-slate-400">
                                                                    {product.branchName} · {formatNumber(product.quantityOnHand)} units
                                                                </p>
                                                            </div>
                                                        </div>
                                                        <div className="text-right flex-shrink-0 ml-4">
                                                            <p className="text-sm font-bold text-slate-900">
                                                                {formatNaira(product.totalValue)}
                                                            </p>
                                                            <p className="text-[10px] text-emerald-600 font-medium">
                                                                +{formatNaira(product.potentialProfit)} profit
                                                            </p>
                                                        </div>
                                                        <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-amber-500 transition-colors ml-2 flex-shrink-0" />
                                                    </motion.div>
                                                ))}
                                            </div>
                                        </CardContent>
                                    </Card>

                                    {/* Right Side Panel - Financial Summary & Additional Stats */}
                                    <div className="space-y-6">
                                        {/* Financial Summary */}
                                        <Card className="overflow-hidden">
                                            <div className="px-6 py-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-blue-100">
                                                <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                                                    <div className="p-1.5 rounded-lg bg-blue-100">
                                                        <Calculator className="w-4 h-4 text-blue-600" />
                                                    </div>
                                                    Financial Summary
                                                </h3>
                                            </div>
                                            <CardContent className="p-6">
                                                <div className="space-y-3">
                                                    <div className="p-4 bg-slate-50 rounded-xl">
                                                        <div className="flex items-center justify-between mb-1">
                                                            <p className="text-xs text-slate-500">Total Cost</p>
                                                            <DollarSign className="w-4 h-4 text-slate-400" />
                                                        </div>
                                                        <p className="text-2xl font-bold text-slate-900">
                                                            {formatNaira(totalStats.totalCost)}
                                                        </p>
                                                        <p className="text-[10px] text-slate-400 mt-1">Inventory investment</p>
                                                    </div>

                                                    <div className="p-4 bg-blue-50 rounded-xl">
                                                        <div className="flex items-center justify-between mb-1">
                                                            <p className="text-xs text-blue-600">Total Retail Value</p>
                                                            <TrendingUp className="w-4 h-4 text-blue-500" />
                                                        </div>
                                                        <p className="text-2xl font-bold text-blue-700">
                                                            {formatNaira(totalStats.totalRetail)}
                                                        </p>
                                                        <p className="text-[10px] text-blue-500 mt-1">
                                                            {totalStats.totalCost > 0 ? `${Math.round((totalStats.totalRetail / totalStats.totalCost) * 100)}% markup` : 'No cost data'}
                                                        </p>
                                                    </div>

                                                    <div className="p-4 bg-emerald-50 rounded-xl">
                                                        <div className="flex items-center justify-between mb-1">
                                                            <p className="text-xs text-emerald-600">Potential Profit</p>
                                                            <TrendingUp className="w-4 h-4 text-emerald-500" />
                                                        </div>
                                                        <p className="text-2xl font-bold text-emerald-700">
                                                            {formatNaira(totalStats.totalProfit)}
                                                        </p>
                                                        <p className="text-[10px] text-emerald-500 mt-1">
                                                            {totalStats.marginPercent}% profit margin
                                                        </p>
                                                    </div>

                                                    {/* Profit Margin Visual */}
                                                    <div className="pt-2">
                                                        <div className="flex items-center justify-between mb-2">
                                                            <p className="text-xs font-medium text-slate-600">Profit Margin</p>
                                                            <span className="text-xs font-bold text-emerald-600">{totalStats.marginPercent}%</span>
                                                        </div>
                                                        <div className="w-full bg-slate-200 rounded-full h-2.5 overflow-hidden">
                                                            <motion.div
                                                                className="bg-gradient-to-r from-emerald-500 to-teal-600 h-2.5 rounded-full"
                                                                initial={{ width: 0 }}
                                                                animate={{ width: `${Math.min(parseFloat(totalStats.marginPercent), 100)}%` }}
                                                                transition={{ duration: 1.5, ease: 'easeOut' }}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </CardContent>
                                        </Card>

                                        {/* Inventory Health */}
                                        <Card>
                                            <CardContent className="p-6">
                                                <h4 className="text-sm font-semibold text-slate-700 mb-4">Inventory Health</h4>
                                                <div className="space-y-3">
                                                    {/* Stock Distribution */}
                                                    <div>
                                                        <div className="flex items-center justify-between mb-1">
                                                            <p className="text-xs text-slate-500">High Value Items</p>
                                                            <span className="text-xs font-bold text-slate-700">
                                                                {valuationData.filter((item: any) => parseFloat(item.totalValue || '0') > 1000000).length}
                                                            </span>
                                                        </div>
                                                        <div className="w-full bg-slate-200 rounded-full h-2 overflow-hidden">
                                                            <div className="bg-gradient-to-r from-amber-500 to-orange-600 h-2 rounded-full"
                                                                style={{ width: `${totalStats.totalProducts > 0 ? (valuationData.filter((item: any) => parseFloat(item.totalValue || '0') > 1000000).length / totalStats.totalProducts) * 100 : 0}%` }} />
                                                        </div>
                                                    </div>

                                                    {/* Average Product Value */}
                                                    <div className="p-3 bg-slate-50 rounded-xl">
                                                        <div className="flex items-center justify-between">
                                                            <p className="text-xs text-slate-500">Avg Product Value</p>
                                                            <p className="text-sm font-bold text-slate-700">
                                                                {totalStats.totalProducts > 0
                                                                    ? formatNaira(totalStats.totalCost / totalStats.totalProducts)
                                                                    : '₦0'}
                                                            </p>
                                                        </div>
                                                    </div>

                                                    {/* Total Units */}
                                                    <div className="p-3 bg-slate-50 rounded-xl">
                                                        <div className="flex items-center justify-between">
                                                            <p className="text-xs text-slate-500">Total Units</p>
                                                            <p className="text-sm font-bold text-slate-700">
                                                                {valuationData.reduce((sum: number, item: any) => sum + parseFloat(item.quantityOnHand || '0'), 0).toLocaleString()}
                                                            </p>
                                                        </div>
                                                    </div>

                                                    {/* Most Valuable Branch */}
                                                    {branchBreakdown.length > 0 && (
                                                        <div className="p-3 bg-emerald-50 rounded-xl">
                                                            <div className="flex items-center justify-between">
                                                                <p className="text-xs text-emerald-600">Top Branch</p>
                                                                <p className="text-sm font-bold text-emerald-700">
                                                                    {branchBreakdown[0].branchName}
                                                                </p>
                                                            </div>
                                                            <p className="text-[10px] text-emerald-500 mt-1">
                                                                {branchBreakdown[0].percentOfTotal}% of total value
                                                            </p>
                                                        </div>
                                                    )}
                                                </div>
                                            </CardContent>
                                        </Card>

                                        {/* Quick Actions */}
                                        <Card>
                                            <CardContent className="p-6">
                                                <h4 className="text-sm font-semibold text-slate-700 mb-3">Quick Actions</h4>
                                                <div className="space-y-2">
                                                    <button
                                                        onClick={() => navigate('/inventory/levels')}
                                                        className="w-full flex items-center justify-between p-3 bg-slate-50 hover:bg-emerald-50 rounded-xl transition-colors group"
                                                        type="button"
                                                    >
                                                        <span className="flex items-center gap-2 text-sm text-slate-600 group-hover:text-emerald-700">
                                                            <Layers className="w-4 h-4" /> View Stock Levels
                                                        </span>
                                                        <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-emerald-600" />
                                                    </button>
                                                    <button
                                                        onClick={() => navigate('/inventory/reorder')}
                                                        className="w-full flex items-center justify-between p-3 bg-slate-50 hover:bg-amber-50 rounded-xl transition-colors group"
                                                        type="button"
                                                    >
                                                        <span className="flex items-center gap-2 text-sm text-slate-600 group-hover:text-amber-700">
                                                            <ShoppingCart className="w-4 h-4" /> Reorder Stock
                                                        </span>
                                                        <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-amber-600" />
                                                    </button>
                                                    <button
                                                        onClick={() => navigate('/inventory/analytics')}
                                                        className="w-full flex items-center justify-between p-3 bg-slate-50 hover:bg-blue-50 rounded-xl transition-colors group"
                                                        type="button"
                                                    >
                                                        <span className="flex items-center gap-2 text-sm text-slate-600 group-hover:text-blue-700">
                                                            <BarChart3 className="w-4 h-4" /> Movement Analytics
                                                        </span>
                                                        <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-blue-600" />
                                                    </button>
                                                </div>
                                            </CardContent>
                                        </Card>
                                    </div>
                                </div>
                            </>
                        ) : (
                            /* All Products View with Pagination */
                            <Card className="overflow-hidden">
                                <div className="px-6 py-4 bg-slate-50/50 border-b border-slate-100">
                                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                                        <div>
                                            <h3 className="font-semibold text-slate-800">
                                                All Products Valuation
                                            </h3>
                                            <p className="text-xs text-slate-500">
                                                {filteredValuationData.length} products
                                                {searchQuery && ` matching "${searchQuery}"`}
                                            </p>
                                        </div>
                                        <div className="relative w-full sm:w-64">
                                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                            <input
                                                type="text"
                                                placeholder="Search products..."
                                                value={searchQuery}
                                                onChange={(e) => {
                                                    setSearchQuery(e.target.value);
                                                    setPage(1);
                                                }}
                                                className="w-full pl-9 pr-8 py-2 bg-white border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400"
                                            />
                                            {searchQuery && (
                                                <button
                                                    onClick={() => {
                                                        setSearchQuery('');
                                                        setPage(1);
                                                    }}
                                                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-lg text-slate-400 hover:text-slate-600"
                                                    type="button"
                                                >
                                                    <X className="w-3.5 h-3.5" />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-slate-100">
                                        <thead>
                                            <tr className="bg-slate-50/50">
                                                <th
                                                    onClick={() => handleSort('productName')}
                                                    className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase cursor-pointer hover:bg-slate-100 transition-colors select-none"
                                                >
                                                    <span className="inline-flex items-center gap-1">
                                                        Product
                                                        {sortBy === 'productName' ? (
                                                            sortOrder === 'asc' ? (
                                                                <ArrowUp className="w-3 h-3" />
                                                            ) : (
                                                                <ArrowDown className="w-3 h-3" />
                                                            )
                                                        ) : (
                                                            <ArrowUpDown className="w-3 h-3 opacity-30" />
                                                        )}
                                                    </span>
                                                </th>
                                                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase hidden sm:table-cell">
                                                    Branch
                                                </th>
                                                <SortableHeader field="quantityOnHand" label="On Hand" />
                                                <SortableHeader field="costPrice" label="Cost Price" />
                                                <SortableHeader field="totalValue" label="Total Value" />
                                                <SortableHeader field="totalRetailValue" label="Retail Value" className="hidden lg:table-cell" />
                                                <SortableHeader field="potentialProfit" label="Profit" />
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-50">
                                            {paginatedData.map((item: any, idx: number) => (
                                                <motion.tr
                                                    key={`${item.productId}-${item.branchId}`}
                                                    initial={{ opacity: 0, y: 10 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    transition={{ delay: Math.min(idx * 0.01, 0.3) }}
                                                    className="hover:bg-slate-50 cursor-pointer transition-colors"
                                                    onClick={() =>
                                                        navigate(
                                                            `/inventory/levels/${item.productId}`
                                                        )
                                                    }
                                                >
                                                    <td className="px-4 py-3">
                                                        <div className="flex items-center gap-2">
                                                            <div className="p-1.5 rounded-lg bg-slate-100">
                                                                <Package className="w-4 h-4 text-slate-500" />
                                                            </div>
                                                            <div>
                                                                <p className="text-sm font-medium text-slate-700">
                                                                    {item.productName || 'Unknown'}
                                                                </p>
                                                                {item.sku && (
                                                                    <p className="text-[10px] text-slate-400 font-mono">
                                                                        {item.sku}
                                                                    </p>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="px-4 py-3 text-sm text-slate-500 hidden sm:table-cell">
                                                        {item.branchName || '—'}
                                                    </td>
                                                    <td className="px-4 py-3 text-right text-sm text-slate-700 tabular-nums">
                                                        {formatNumber(item.quantityOnHand)}
                                                    </td>
                                                    <td className="px-4 py-3 text-right text-sm text-slate-600 tabular-nums">
                                                        {formatNaira(item.costPrice)}
                                                    </td>
                                                    <td className="px-4 py-3 text-right text-sm font-bold text-slate-900 tabular-nums">
                                                        {formatNaira(item.totalValue)}
                                                    </td>
                                                    <td className="px-4 py-3 text-right text-sm font-bold text-slate-700 hidden lg:table-cell tabular-nums">
                                                        {formatNaira(item.totalRetailValue)}
                                                    </td>
                                                    <td className="px-4 py-3 text-right text-sm font-bold text-emerald-600 tabular-nums">
                                                        {formatNaira(item.potentialProfit)}
                                                    </td>
                                                </motion.tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                <Pagination
                                    currentPage={page}
                                    totalPages={totalPages}
                                    totalItems={filteredValuationData.length}
                                    pageSize={pageSize}
                                    onPageChange={setPage}
                                    onPageSizeChange={(size) => {
                                        setPageSize(size);
                                        setPage(1);
                                    }}
                                />
                            </Card>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}